/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.23.0
 */

import { useCallback, useState } from 'react';

import { MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { MutationHooks } from '@reduxjs/toolkit/dist/query/react/buildHooks';

export interface IInfiniteMutation {
    searchValue?: string;
    start?: boolean;
    from?: number;
    size?: number;
}

export function useInfiniteMutation<Item, QueryArg extends IInfiniteMutation = IInfiniteMutation>(
    endpoint: MutationHooks<MutationDefinition<QueryArg, any, string, { count: number; elements: Item[] }>>,
) {
    const [pages, setPages] = useState<Item[]>([]);
    const [count, setCount] = useState(0);
    const [trigger, result] = endpoint.useMutation();

    const fetchNextPage = useCallback(
        (query: QueryArg) => {
            trigger({ ...query, from: query.start ? 0 : pages.length })
                .unwrap()
                .then((data) => {
                    if (!data) return;

                    if (!count) setCount(data.count);

                    if (query.start) {
                        setPages(data.elements);
                    }
                    if (!query.start) {
                        setPages((page) => page.concat(data.elements));
                    }
                })
                .catch(() => {
                    setPages([]);
                });
        },
        [count, pages.length, trigger],
    );

    return {
        ...result,
        data: pages,
        isLoading: result.isLoading,
        hasMore: result.data?.count !== pages.length,
        fetchNextPage,
        count,
    };
}
